import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product, Product_ProductUnitUsage } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Transfer_Product as TransferProduct } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import {
  defaultUnitOfMeasureCode,
  defaultUnitOfMeasureLabel,
  getConversionFactorForUom,
  isValidUomSnapshot as isValidUomSnapshotShared,
  safeConvertQuantityToUoMQuantity,
  validateUomSelection,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { UnitOfMeasure } from "shared-ts/libs/product/unitsOfMeasure.types"
import { logger } from "shared-ts/logger"

const uomLogger = logger.withScope("pos/transfers/uom")

export interface TransferUomInfo {
  /** Present only when UOM snapshot validates. */
  uomId?: PBUUID
  conversionFactorToBase: Decimal
  displayLabel: string
  displayCode: string
}

export function getTransferDefaultUomId(product?: Product): PBUUID | undefined {
  const uomId =
    product?.productDefaultUom?.[Product_ProductUnitUsage.TRANSFER]?.uomId
  if (!uomId) return undefined

  const factor = getConversionFactorForUom(product, uomId)
  return factor && factor.gt(Decimal.zero()) ? uomId : undefined
}

export function isValidUomSnapshot(
  snapshot?: LedgerEntry_UomSnapshot
): snapshot is LedgerEntry_UomSnapshot {
  return isValidUomSnapshotShared(snapshot)
}

export function getTransferUomInfo(args: {
  transferProduct?: TransferProduct
}): TransferUomInfo {
  const { transferProduct } = args
  const uomId = transferProduct?.uomId
  const snap = transferProduct?.uomSnapshot

  // Snapshot policy: missing/invalid snapshot => base-only.
  if (!uomId || !isValidUomSnapshot(snap) || !snap.conversionFactorToBase) {
    return {
      uomId: undefined,
      conversionFactorToBase: Decimal.fromBigSource(1),
      displayLabel: defaultUnitOfMeasureLabel,
      displayCode: defaultUnitOfMeasureCode,
    }
  }

  const factor = Decimal.fromPB(snap.conversionFactorToBase)
  return {
    uomId,
    conversionFactorToBase: factor,
    displayLabel: `${snap.uomName} (${snap.uomCode})`,
    displayCode: snap.uomCode,
  }
}

export function buildValidatedTransferUomSelection(args: {
  product?: Product
  uomId?: PBUUID
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): { uomId?: PBUUID; uomSnapshot?: LedgerEntry_UomSnapshot } {
  const { product, uomId, companyUnitsOfMeasureById } = args

  const result = validateUomSelection({
    product,
    uomId,
    companyUnitsOfMeasureById,
  })
  if (!result.ok) {
    if (result.reason !== "missing_uom_id") {
      uomLogger.error("Invalid UOM selection; falling back to base.", {
        reason: result.reason,
        productIdString: UUID.idString(product?.id),
        uomIdString: UUID.idString(uomId),
      })
    }
    return { uomId: undefined, uomSnapshot: undefined }
  }

  return { uomId: result.uomId, uomSnapshot: result.uomSnapshot }
}

export function stripTransferProductUomFields(
  transferProduct: TransferProduct
): TransferProduct {
  const {
    uomId: _uomId,
    uomSnapshot: _uomSnapshot,
    quantitySentUom: _quantitySentUom,
    quantityReceivedUom: _quantityReceivedUom,
    quantityRequestedUom: _quantityRequestedUom,
    ...rest
  } = transferProduct
  return rest
}

export function applyTransferUomSelection(args: {
  transferProduct: TransferProduct
}): TransferProduct {
  const { transferProduct } = args
  const ctx = getTransferUomInfo({ transferProduct })

  const sentV2 = Decimal.fromPB(
    transferProduct.quantitySentV2 ?? Decimal.zero()
  )
  const receivedV2 = Decimal.fromPB(
    transferProduct.quantityReceivedV2 ?? Decimal.zero()
  )
  const requestedV2 = Decimal.fromPB(
    transferProduct.quantityRequestedV2 ?? Decimal.zero()
  )

  if (!ctx.uomId) {
    // Ensure base quantities stay normalized, and strip UOM wrapper fields.
    return stripTransferProductUomFields({
      ...transferProduct,
      quantitySentV2: sentV2,
      quantityReceivedV2: receivedV2,
      quantityRequestedV2: requestedV2,
    })
  }

  return {
    ...transferProduct,
    quantitySentUom: safeConvertQuantityToUoMQuantity(
      sentV2,
      ctx.conversionFactorToBase
    ),
    quantityReceivedUom: safeConvertQuantityToUoMQuantity(
      receivedV2,
      ctx.conversionFactorToBase
    ),
    quantityRequestedUom: safeConvertQuantityToUoMQuantity(
      requestedV2,
      ctx.conversionFactorToBase
    ),
    quantitySentV2: sentV2,
    quantityReceivedV2: receivedV2,
    quantityRequestedV2: requestedV2,
  }
}

export function sanitizeTransferProductForUpsert(
  transferProduct: TransferProduct
): TransferProduct {
  if (!transferProduct.uomId)
    return stripTransferProductUomFields(transferProduct)

  if (!isValidUomSnapshot(transferProduct.uomSnapshot)) {
    uomLogger.error(
      "Transfer product has invalid/missing UOM snapshot; omitting UOM fields for upsert.",
      {
        productIdString: UUID.idString(transferProduct.productId),
        uomIdString: UUID.idString(transferProduct.uomId),
      }
    )
    return stripTransferProductUomFields(transferProduct)
  }

  return applyTransferUomSelection({ transferProduct })
}
